import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

interface VisualEditorUrlState {
  editorStep: string;
  editorMode: 'edit' | 'create';
}

interface PendingRestore {
  stepId: string;
  mode: 'edit' | 'create';
}

interface UseVisualEditorUrlSyncProps {
  /** Whether funnel data has finished loading (so we can safely restore from URL) */
  isFunnelLoaded: boolean;
  /** Current steps from the funnel (used to validate step existence) */
  steps: Array<{
    id: string;
  }>;
}

/**
 * Syncs the visual editor (Studio iframe) state with URL query params.
 *
 * URL params:
 *   editor=studio      — visual editor is open
 *   editorStep={id}    — step being edited
 *   editorMode=create  — creating new design (omitted for edit mode)
 *
 * Returns:
 *   - setEditorParams / clearEditorParams: call when opening/closing the editor
 *   - pendingRestore: a one-shot value that appears when the page loads with
 *     editor URL params — the consumer should open the editor and call `consumeRestore()`
 */
export function useVisualEditorUrlSync({
  isFunnelLoaded,
  steps,
}: UseVisualEditorUrlSyncProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const hasRestoredRef = useRef(false);
  const isUpdatingRef = useRef(false);
  const [pendingRestore, setPendingRestore] = useState<PendingRestore | null>(null);

  // ─── Detect restore-from-URL on mount (once funnel is loaded) ───
  useEffect(() => {
    if (hasRestoredRef.current || !isFunnelLoaded || steps.length === 0) return;

    const editor = searchParams.get('editor');
    const editorStep = searchParams.get('editorStep');
    const editorMode = searchParams.get('editorMode') as 'edit' | 'create' | null;

    if (editor !== 'studio' || !editorStep) {
      hasRestoredRef.current = true;
      return;
    }

    const stepExists = steps.some((s) => s.id === editorStep);
    if (!stepExists) {
      hasRestoredRef.current = true;
      clearEditorParamsInternal();
      return;
    }

    hasRestoredRef.current = true;
    setPendingRestore({ stepId: editorStep, mode: editorMode || 'edit' });
  }, [isFunnelLoaded, steps, searchParams]);

  // ─── Write to URL ───────────────────────────────────────────────

  const setEditorParams = useCallback(
    (state: VisualEditorUrlState) => {
      if (isUpdatingRef.current) return;
      isUpdatingRef.current = true;
      hasRestoredRef.current = true;

      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('editor', 'studio');
          next.set('editorStep', state.editorStep);
          if (state.editorMode === 'create') {
            next.set('editorMode', 'create');
          } else {
            next.delete('editorMode');
          }
          next.delete('stepId');
          return next;
        },
        { replace: true },
      );

      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 50);
    },
    [setSearchParams],
  );

  const clearEditorParamsInternal = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('editor');
        next.delete('editorStep');
        next.delete('editorMode');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const clearEditorParams = useCallback(() => {
    isUpdatingRef.current = true;

    clearEditorParamsInternal();

    // Use double-rAF to ensure the URL update has been flushed to the browser
    // before allowing other URL writes (more reliable than a fixed timeout)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        isUpdatingRef.current = false;
      });
    });
  }, [clearEditorParamsInternal]);

  const consumeRestore = useCallback(() => {
    setPendingRestore(null);
  }, []);

  return { setEditorParams, clearEditorParams, pendingRestore, consumeRestore };
}
